import React, { Fragment, useEffect, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import {
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Menu as MenuIcon,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { Company } from '../../types';
import { APP_DEFAULT_SITE_NAME } from '../../constants/app';
import { getLogoUrl, getProfilePhotoUrl } from '../../utils/formatters';
import WeatherWidget from '../ui/WeatherWidget';

const UserAvatar: React.FC<{ user: NonNullable<ReturnType<typeof useAuth>['user']> }> = ({ user }) => {
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);
  const photoUrl = user.profilePhotoUrl ? getProfilePhotoUrl(user.profilePhotoUrl) : '';
  const initials = `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`;

  useEffect(() => {
    setPhotoLoadFailed(false);
  }, [user.profilePhotoUrl]);

  if (!user.profilePhotoUrl || photoLoadFailed) {
    return (
      <div className="h-8 w-8 rounded-full bg-accent-color flex items-center justify-center">
        <span className="text-white font-medium text-sm">{initials}</span>
      </div>
    );
  }

  return (
    <div className="h-8 w-8 rounded-full border-2 border-accent-color overflow-hidden">
      <img
        src={photoUrl}
        alt={`${user.firstName} ${user.lastName}`}
        className="h-full w-full rounded-full object-cover"
        onError={() => setPhotoLoadFailed(true)}
      />
    </div>
  );
};

interface HeaderProps {
  company?: Company;
  onMobileMenuToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ company, onMobileMenuToggle }) => {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch {}
    logout();
  };

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo and mobile menu button */}
          <div className="flex items-center">
            <button
              type="button"
              className="lg:hidden p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-accent-color hover:bg-accent-color-50 dark:hover:bg-accent-color-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-color"
              onClick={onMobileMenuToggle}
            >
              <span className="sr-only">Open sidebar</span>
              <MenuIcon className="h-6 w-6" />
            </button>
            
            <div className="flex items-center">
              {company?.logoUrl ? (
                <>
                  <div
                    className="h-8 w-8 rounded-md bg-accent-color flex items-center justify-center mr-3"
                    style={{ display: 'none' }}
                  >
                    <span className="text-white font-medium text-sm">
                      {company.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '??'}
                    </span>
                  </div>
                  <img
                    className="h-8 w-auto"
                    src={getLogoUrl(company.logoUrl)}
                    alt={company.name}
                    onError={(e) => {
                      // Hide the img and show the initials div
                      e.currentTarget.style.display = 'none';
                      const initialsDiv = e.currentTarget.previousElementSibling as HTMLElement;
                      if (initialsDiv) {
                        initialsDiv.style.display = 'flex';
                      }
                    }}
                  />
                </>
              ) : company?.name ? (
                <div
                  className="h-8 w-8 rounded-md bg-accent-color flex items-center justify-center mr-3"
                >
                  <span className="text-white font-medium text-sm">
                    {company.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </span>
                </div>
              ) : (
                <img
                  className="h-8 w-auto"
                  src="/WorkOrderHQLOGO.png"
                  alt="WorkOrder HQ"
                />
              )}
              <span className="ml-3 text-lg font-semibold text-gray-900 dark:text-gray-100 hidden sm:block">
                {company?.name || APP_DEFAULT_SITE_NAME}
              </span>
            </div>
          </div>

          {/* Right side - Weather, Notifications, Help and user menu */}
          <div className="flex items-center space-x-4">
            <WeatherWidget userCity={user?.city} companyCity={company?.city} />
            {/* Notifications */}
            <NotificationBell />
            
            {/* Help */}
            <Link
              to="/help"
              className="p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-accent-color hover:bg-accent-color-50 dark:hover:bg-accent-color-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-color"
              title="Help Center"
            >
              <span className="sr-only">Help</span>
              <HelpCircle className="h-6 w-6" />
            </Link>

            {/* User menu */}
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-color dark:focus:ring-offset-gray-900">
                <span className="sr-only">Open user menu</span>
                {user ? <UserAvatar user={user} /> : null}
                <span className="ml-2 text-gray-700 dark:text-gray-300 hidden sm:block">
                  {user?.firstName} {user?.lastName}
                </span>
                <ChevronDown className="ml-1 h-4 w-4 text-gray-400 dark:text-gray-500 hidden sm:block" />
              </Menu.Button>
              
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none z-50">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className={`${
                            active ? 'bg-accent-color-50 dark:bg-accent-color-800 text-accent-color-700 dark:text-accent-color-300' : 'text-gray-700 dark:text-gray-300'
                          } flex items-center px-4 py-2 text-sm`}
                        >
                          <User className="mr-3 h-4 w-4" />
                          Profile
                        </Link>
                      )}
                    </Menu.Item>
                    
                    {user?.role?.name === 'Admin' && (
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="/admin"
                            className={`${
                              active ? 'bg-accent-color-50 dark:bg-accent-color-800 text-accent-color-700 dark:text-accent-color-300' : 'text-gray-700 dark:text-gray-300'
                            } flex items-center px-4 py-2 text-sm`}
                          >
                            <Settings className="mr-3 h-4 w-4" />
                            Admin Settings
                          </a>
                        )}
                      </Menu.Item>
                    )}
                    
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-accent-color-50 dark:bg-accent-color-800 text-accent-color-700 dark:text-accent-color-300' : 'text-gray-700 dark:text-gray-300'
                          } flex items-center w-full px-4 py-2 text-sm`}
                        >
                          <LogOut className="mr-3 h-4 w-4" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
  const isSuperAdmin = isSystemAdmin || user?.roleName === 'SuperAdmin';
  
  const [count, setCount] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<any[]>([]);

  const fetchCount = async () => {
    if (isSuperAdmin) {
      setCount(0);
      return;
    }
    try {
      const res = await apiService.getUnreadNotificationCount();
      setCount(res.data ?? 0);
    } catch {}
  };

  const fetchNotifications = async () => {
    if (isSuperAdmin) {
      setNotifications([]);
      return;
    }
    try {
      const res = await apiService.getMyNotifications(1, 5); // Get first 5 notifications
      setNotifications(res.data?.data || []);
    } catch {}
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      fetchCount();
      const id = setInterval(fetchCount, 30000);
      return () => clearInterval(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  const handleNotificationClick = async (notificationId: number, invoiceId?: number) => {
    try {
      await apiService.markNotificationRead(notificationId);
      setCount(prev => Math.max(0, prev - 1));
      fetchNotifications();
      
      if (invoiceId) {
        // Navigate to invoice edit page
        window.location.href = `/invoices/${invoiceId}/edit`;
      }
    } catch {}
  };

  const renderNotificationMessage = (notification: any) => {
    const message = notification.notification.message;
    
    // Check if this is an invoice notification
    if (notification.notification.notificationType === 'InvoiceOverdue' || 
        notification.notification.notificationType === 'InvoiceDueSoon') {
      
      // Extract invoice number from message (e.g., "Invoice #INV-00036 is overdue by 4 days")
      const invoiceMatch = message.match(/Invoice #([A-Z0-9-]+)/);
      if (invoiceMatch && notification.notification.invoiceId) {
        const invoiceNumber = invoiceMatch[1];
        const beforeInvoice = message.substring(0, message.indexOf('Invoice #'));
        const afterInvoice = message.substring(message.indexOf(invoiceNumber) + invoiceNumber.length);
        
        return (
          <span>
            {beforeInvoice}
            <button
              onClick={() => handleNotificationClick(notification.id, notification.notification.invoiceId)}
              className="text-accent-color-600 hover:text-accent-color-800 dark:text-accent-color-400 dark:hover:text-accent-color-300 font-medium underline"
            >
              Invoice #{invoiceNumber}
            </button>
            {afterInvoice}
          </span>
        );
      }
    }
    
    // Default message rendering
    return <span>{message}</span>;
  };

  // Don't show notification bell for superadmins
  if (isSuperAdmin) {
    return null;
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-accent-color hover:bg-accent-color-50 dark:hover:bg-accent-color-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-color"
      >
        <span className="sr-only">View notifications</span>
        <Bell className="h-6 w-6" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium leading-none text-white bg-red-600 rounded-full">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 z-50">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Notifications</div>
            <button
              onClick={async () => {
                await apiService.markAllNotificationsRead();
                setCount(0);
                fetchNotifications();
              }}
              className="text-xs text-accent-color dark:text-accent-color-400 hover:text-accent-color-800 dark:hover:text-accent-color-300"
            >
              Mark all as read
            </button>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 dark:text-gray-400">No notifications</div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                    !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification.id, notification.notification.invoiceId)}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {notification.notification.title}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                    {renderNotificationMessage(notification)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(notification.notification.createdDate).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-2 border-t text-center">
            <a href="/notifications" className="text-sm text-accent-color dark:text-accent-color-400 hover:text-accent-color-800 dark:hover:text-accent-color-300">View all</a>
          </div>
        </div>
      )}
    </div>
  );
};
